import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import type { Task, Metrics } from "@/types/dashboard";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Calculate metrics from tasks object - the single source of truth
 * This ensures consistency between task list and summary stats
 */
export function calculateMetricsFromTasks(tasks: Record<string, Task>, totalQuestions?: number): Metrics {
  const taskArray = Object.values(tasks);

  const running = taskArray.filter(t => t.status === "running").length;
  const completed = taskArray.filter(t => t.status === "completed" || t.status === "success").length;
  const failed = taskArray.filter(t => t.status === "failed" || t.status === "error").length;
  const queued = taskArray.filter(t => t.status === "queued").length;

  // Get scores from completed tasks
  const scores = taskArray
    .filter(t => t.accuracy_score !== undefined && t.accuracy_score !== null)
    .map(t => t.accuracy_score!);

  const mean_accuracy = scores.length > 0
    ? scores.reduce((a, b) => a + b, 0) / scores.length
    : 0;

  // SQL success rate
  const total_evaluated = completed + failed;
  const sql_valid_count = taskArray.filter(t => t.sql_valid).length;
  const sql_success_rate = total_evaluated > 0
    ? (sql_valid_count / total_evaluated) * 100
    : 0;

  return {
    total: totalQuestions || taskArray.length,
    completed,
    running,
    queued,
    failed,
    success: completed,
    mean_accuracy: Math.round(mean_accuracy * 100) / 100,
    sql_success_rate: Math.round(sql_success_rate * 10) / 10,
    scores
  };
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
}

export function formatPercentage(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

export function calculateETA(completed: number, total: number, elapsedMs: number): string {
  if (completed === 0) return "Calculating...";
  const avgTimePerTask = elapsedMs / completed;
  const remaining = total - completed;
  const etaMs = remaining * avgTimePerTask;
  return formatDuration(etaMs);
}
